import React from "react"
import { useTranslation } from "react-i18next"
import { Analytics } from "@vercel/analytics/react"
import { Header, Footer } from "./LandingPage"
import { Github, Book, Share2, Server, GitFork } from "lucide-react"
import SEO from './common/SEO'

interface ProjectCardProps {
  title: string;
  description: string;
  githubUrl: string;
  techStack: string[];
  license: string;
  stars: number;
  forks: number;
}

const ProjectCard: React.FC<ProjectCardProps> = ({
  title,
  description,
  githubUrl,
  techStack,
  license,
  stars,
  forks
}) => {
  return (
    <div className="panel-tint-blue rounded-[2rem] p-8">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <a
            href={githubUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-2xl font-bold hover:text-[#1F80FF] transition-colors"
          >
            {title}
          </a>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1 text-muted-foreground">
              <svg className="w-4 h-4" viewBox="0 0 16 16" fill="currentColor">
                <path d="M8 .25a.75.75 0 01.673.418l1.882 3.815 4.21.612a.75.75 0 01.416 1.279l-3.046 2.97.719 4.192a.75.75 0 01-1.088.791L8 12.347l-3.766 1.98a.75.75 0 01-1.088-.79l.72-4.194L.818 6.374a.75.75 0 01.416-1.28l4.21-.611L7.327.668A.75.75 0 018 .25z"/>
              </svg>
              <span>{stars}</span>
            </div>
            <div className="flex items-center gap-1 text-muted-foreground">
              <GitFork className="w-4 h-4" />
              <span>{forks}</span>
            </div>
            <a
              href={githubUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-muted-foreground hover:text-[#1F80FF] transition-colors"
            >
              <Github className="w-6 h-6" />
            </a>
          </div>
        </div>
        <p className="text-muted-foreground text-lg">
          {description}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <span className="bg-[#1F80FF]/10 text-[#1F80FF] px-3 py-1 rounded-full text-sm">
            {license}
          </span>
          {techStack.map((tech, index) => (
            <span 
              key={index}
              className="bg-[#1F80FF]/10 text-[#1F80FF] px-3 py-1 rounded-full text-sm"
            >
              {tech}
            </span>
          ))}
        </div>
      </div>
    </div>
  )
}

const ResearchPage: React.FC = () => {
  const { t } = useTranslation();

  const projects: ProjectCardProps[] = [
    {
      title: "LibDC-Swift",
      description: t('research.projects.items.libdcSwift.description'),
      githubUrl: "https://github.com/deepsealabs/libdc-swift",
      techStack: ["Swift", "BLE", "libdivecomputer", "iOS", "macOS"],
      license: "LGPL-2.1",
      stars: 10,
      forks: 13
    },
    {
      title: "FIT Parser Swift",
      description: t('research.projects.items.fitParserSwift.description'),
      githubUrl: "https://github.com/deepsealabs/fit-parser-swift",
      techStack: ["Swift", "FIT Protocol", "SwiftUI", "XML"],
      license: "MIT",
      stars: 3,
      forks: 1
    }
  ];

  const [liveStats, setLiveStats] = React.useState<Record<string, { stars: number; forks: number }>>({});

  React.useEffect(() => {
    let cancelled = false;
    projects.forEach(async (project) => {
      const repoPath = project.githubUrl.replace('https://github.com/', '');
      try {
        const res = await fetch(`https://api.github.com/repos/${repoPath}`);
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) {
          setLiveStats(prev => ({
            ...prev,
            [project.githubUrl]: { stars: data.stargazers_count, forks: data.forks_count }
          }));
        }
      } catch {
        // Keep the last-known static values if the GitHub API is unreachable
      }
    });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="flex min-h-screen flex-col relative">
      <SEO path="/research" />
      <Header />
      <main className="flex-1">
        {/* Hero Section */}
        <section className="px-3 pt-3">
          <div className="panel-hero rounded-[2.5rem]">
          <div className="container flex flex-col items-center justify-center space-y-5 pt-40 md:pt-48 pb-20 text-center">
            <h1 className="max-w-3xl text-4xl md:text-6xl font-black tracking-tighter leading-[0.95]">
              {t('research.hero.titleLine1')}<br />
              <span className="text-[#1F80FF]">{t('research.hero.titleLine2')}</span>
            </h1>
            <p className="max-w-[700px] text-lg md:text-xl text-muted-foreground">
              {t('research.hero.description')}
            </p>
          </div>
          </div>
        </section>

        {/* Philosophy Section */}
        <section className="container py-20">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-[1200px] mx-auto">
            <div className="panel-tint-blue rounded-[2rem] p-8">
              <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mb-4 shadow-sm">
                <Share2 className="w-6 h-6 text-[#1F80FF]" />
              </div>
              <h3 className="text-xl font-bold mb-2">{t('research.philosophy.openSource.title')}</h3>
              <p className="text-muted-foreground">
                {t('research.philosophy.openSource.description')}
              </p>
            </div>

            <div className="panel-tint-lavender rounded-[2rem] p-8">
              <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mb-4 shadow-sm">
                <Book className="w-6 h-6 text-[#1F80FF]" />
              </div>
              <h3 className="text-xl font-bold mb-2">{t('research.philosophy.documentation.title')}</h3>
              <p className="text-muted-foreground">
                {t('research.philosophy.documentation.description')}
              </p>
            </div>

            <div className="panel-tint-mint rounded-[2rem] p-8">
              <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mb-4 shadow-sm">
                <Server className="w-6 h-6 text-[#1F80FF]" />
              </div>
              <h3 className="text-xl font-bold mb-2">{t('research.philosophy.sustainability.title')}</h3>
              <p className="text-muted-foreground">
                {t('research.philosophy.sustainability.description')}
              </p>
            </div>
          </div>
        </section>

        {/* Projects Grid */}
        <section className="container py-20">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-black tracking-tight">{t('research.projects.heading')}</h2>
            <p className="mt-4 text-lg text-muted-foreground">
              {t('research.projects.subheading')}
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-[1200px] mx-auto">
            {projects.map((project, index) => (
              <ProjectCard
                key={index}
                {...project}
                stars={liveStats[project.githubUrl]?.stars ?? project.stars}
                forks={liveStats[project.githubUrl]?.forks ?? project.forks}
              />
            ))}
          </div>
        </section>

        {/* Call to Action */}
        <section className="container py-20 text-center">
          <div className="max-w-[800px] mx-auto space-y-8">
            <h2 className="text-3xl md:text-4xl font-black tracking-tight">{t('research.cta.heading')}</h2>
            <p className="text-lg text-muted-foreground">
              {t('research.cta.description')}
            </p>
            <div className="flex justify-center">
              <a
                href="https://github.com/deepsealabs"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-2 bg-[#1F80FF] text-white px-6 py-3 rounded-full hover:bg-[#1F80FF]/90"
              >
                <Github className="w-5 h-5" />
                <span>{t('research.cta.button')}</span>
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <Analytics />
    </div>
  )
}

export default ResearchPage